import { setError } from '@shared/store/slices/media.slice';
import { store } from '@shared/store/store';
import { PlayerError, PlayerType } from '@shared/types/enums/player';
import PlayerUtils from '@shared/utils/player';
import Youbora from '../../../../classes/youbora';
import { checkState } from './mediaPlayer';
import AnalyticsInstance from '@shared/utils/analytics';

function base64ToBytes(base64: string) {
  return Uint8Array.from(atob(base64), (m) => m.codePointAt(0));
}

function bytesToBase64(bytes: Uint8Array<ArrayBuffer>) {
  return btoa(Array.from(bytes, (byte) => String.fromCodePoint(byte)).join(''));
}

const setListeners = ({
  headers,
  drm,
  updateData,
  setCue,
  youboraInstance,
}: {
  headers: {
    accessToken: string | null | undefined;
    contentType: string;
    deviceId: string;
    planId: string | undefined;
    platform: string;
    userId: string | undefined;
  };
  drm?: { license?: string };
  updateData: () => void;
  setCue: React.Dispatch<React.SetStateAction<string>>;
  youboraInstance: React.MutableRefObject<Youbora | null>;
}) => {
  const Listener = {
    onbufferingcomplete: function () {
      if (checkState(['READY', 'PLAYING', 'PAUSED'])) {
        try {
          webapis.avplay.play();
        } catch (e) {
          console.error('Error while trying to play after buffering state', e);
        }
      }
      if (youboraInstance.current?.adapter?.onbufferingcomplete) {
        youboraInstance.current.adapter.onbufferingcomplete();
      }
      if (youboraInstance?.current?.adapter.fireJoin) {
        youboraInstance.current.adapter.fireJoin();
      }
    },
    oncurrentplaytime: function (/*currentTime: number*/) {
      updateData();
      if (youboraInstance.current?.adapter?.oncurrentplaytime) {
        youboraInstance.current.adapter.oncurrentplaytime();
      }
    },
    onerror: function (eventType: string) {
      AnalyticsInstance.getOmnitrackManager()?.forceError(eventType, '{}');
      if (youboraInstance.current?.adapter?.onerror) {
        youboraInstance.current.adapter.onerror();
      }
    },
    onerrormsg: function (eventType: string, eventMsg: string) {
      const errorToSend = {
        code: `${eventType}`,
        player: PlayerType.AVPLAYER,
        message: eventMsg,
      };
      store.dispatch(setError(errorToSend));
      AnalyticsInstance.getOmnitrackManager()?.forceError(eventType, eventMsg);
      if (youboraInstance?.current?.adapter.fireError) {
        youboraInstance.current.adapter.fireError(PlayerError.MEDIA, eventMsg);
      }
    },
    onsubtitlechange: function (
      duration: number,
      text: string
      /*data3: unknown,
      data4: unknown*/
    ) {
      setCue(text);
    },
    ondrmevent: function (
      type: string,
      data: { name: string; challenge: string; session_id: string }
    ) {
      if (data.name == 'Challenge') {
        const { accessToken, contentType, deviceId, planId, platform, userId } =
          headers;
        if (drm?.license) {
          fetch(drm?.license, {
            method: 'POST',
            headers: {
              'content-type': contentType,
              'x-access-token': accessToken,
              'x-tvup-device': deviceId,
              'x-tvup-planid': planId,
              'x-tvup-platform': platform,
              'x-tvup-userid': userId,
            } as unknown as { contentType: string },
            body: base64ToBytes(data.challenge),
          }).then(async (response) => {
            if (response?.ok) {
              response.arrayBuffer().then((arrData) => {
                const myarray = new Uint8Array(arrData);
                const LicenseParam =
                  data.session_id +
                  'PARAM_START_POSITION' +
                  bytesToBase64(myarray) +
                  'PARAM_START_POSITION';
                if (checkState(['IDLE']))
                  try {
                    webapis.avplay.setDrm(
                      'EME_WIDEVINE_CDM',
                      'widevine_license_data',
                      LicenseParam
                    );
                  } catch (e) {
                    console.error(
                      'Error while trying to set drm after requesting license',
                      e
                    );
                  }
              });
            } else {
              console.error(response);
              const finalErrorMsg = PlayerUtils.getAvPlayerError(
                PlayerError.DRM,
                response.status
              );
              const errorToSend = {
                code: `${response?.status}`,
                player: PlayerType.AVPLAYER,
                message: finalErrorMsg,
              };
              store.dispatch(setError(errorToSend));
              if (youboraInstance?.current?.adapter.fireError) {
                youboraInstance.current.adapter.fireError(
                  PlayerError.DRM,
                  'DRM ' + response?.status
                );
              }
            }
          });
        }
      }
    },
    onstreamcompleted: function () {
      if (youboraInstance.current?.adapter?.onstreamcompleted) {
        youboraInstance.current.adapter.onstreamcompleted();
      }
    },
  };
  try {
    webapis.avplay.setListener(Listener);
  } catch (e) {
    console.error('Error while adding listeners', e);
  }
};

export default setListeners;
